import {COLORS, WIDTH, HEIGHT, dimensions} from './model.mjs';

export const WRITING_INSTRUCTION_STRINGS = {'story-studio':910003, 'writing-assistant':910004};

export const writingKind = value => value === 'writing-assistant' ? 'writing-assistant' : 'story-studio';
const clone = value => JSON.parse(JSON.stringify(value));
export function writingIdentity({activityId, segmentId, language = 'en', writingActivity}) {
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,150}$/.test(String(activityId || ''))) throw Error('Missing activity identity.');
  if (!segmentId || typeof segmentId !== 'string' || segmentId.length > 200) throw Error('Missing segment identity.');
  if (!/^[a-z]{2,3}(?:[-_][a-zA-Z0-9]{2,8})*$/.test(language)) throw Error('Invalid writing language.');
  const segmentKey = Array.from(new TextEncoder().encode(segmentId), byte => byte.toString(16).padStart(2, '0')).join('');
  const kind = writingKind(writingActivity);
  return {kind, key:`${language}:${segmentKey}:${kind}`, storageActivityId:`${activityId}/math-rods-writing/${language}/${segmentKey}/${kind}`};
}
const hasText = html => String(html || '').replace(/<[^>]*>/g, '').replace(/&(?:nbsp|#160|#xA0);/gi, ' ').trim().length > 0;
export function hasWritingContent(document, kind) {
  if (!document) return false;
  if (writingKind(kind) === 'writing-assistant') return hasText(document.text) || /<img\b[^>]*\bsrc\s*=/i.test(document.text || '');
  return Array.isArray(document.pages) && document.pages.some(page => page && (hasText(page.text) || page.image || page.audio));
}
export function createWritingDocument(kind, image) {
  if (!/^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(image || '')) throw Error('Expected a local PNG workspace capture.');
  return writingKind(kind) === 'writing-assistant'
    ? {templateAlias:'WRITING_ASSISTANT', workspaceCapture:{version:3}, text:`<p><img src="${image}" alt="Activity workspace"></p><p><br></p>`}
    : {templateAlias:'STORY_STUDIO', workspaceCapture:{version:3}, pages:[{image, text:''}]};
}
export function chooseWritingDocument({kind, cached, stored, capture}) {
  // An explicitly emptied current document wins over older stored content.
  const existing = cached !== undefined ? cached : stored;
  const addition = createWritingDocument(kind, capture());
  if (!hasWritingContent(existing, kind)) return addition;
  const document = clone(existing);
  document.workspaceCapture = {...document.workspaceCapture, version:3};
  if (writingKind(kind) === 'writing-assistant') document.text = `${document.text || ''}${addition.text}`;
  else document.pages = [...(document.pages || []), addition.pages[0]];
  return document;
}
export function writingActivityFor(parent, session) {
  return {
    ...parent,
    activity:{...parent.activity, _id:session.storageActivityId, monolingual:true, currentLanguageVersion:{version:'',locale:''}},
    data:{templateAlias:session.kind === 'writing-assistant' ? 'WRITING_ASSISTANT' : 'STORY_STUDIO',
      options:{...(parent.data?.options || {}),storyStudioMode:'EDITOR_ONLY', needApproval:false, aiFeedbackEnabled:false,
        editor:{fonts:['Arial'],fontSizes:['normal','h2','h1'],fontColors:['black','blue','red'],fontStyles:[],alignments:['left']}},
      gameData:{pagesCount:Math.max(1,session.document?.pages?.length || 0),autoInsertArt:false,importClipart:false,images:[],words:[],
        instructionString:WRITING_INSTRUCTION_STRINGS[session.kind],
        guides:session.kind === 'writing-assistant' ? [{string:WRITING_INSTRUCTION_STRINGS[session.kind]}] : [],starters:[],reading:''},languageVersionData:{}},
  };
}
// Capture only the arrangement with a one-square margin, using the actual on-screen
// rod unit. This is display data: authored rod coordinates are never changed.
export function writingCaptureBounds({rods = [], chart = false, unitPixels = 40}) {
  if (!rods.length) throw Error('Place rods before opening the Writing Activity.');
  const unit = Number.isFinite(unitPixels) && unitPixels > 0 ? unitPixels : 40;
  const bounds = rods.reduce((box, rod) => {
    const [w,h] = dimensions(rod);
    return {left:Math.min(box.left,rod.x),top:Math.min(box.top,rod.y),right:Math.max(box.right,rod.x+w),bottom:Math.max(box.bottom,rod.y+h)};
  },{left:WIDTH,top:HEIGHT,right:0,bottom:0});
  const left = chart ? 0 : Math.max(0,bounds.left-1);
  const right = chart ? WIDTH : Math.min(WIDTH,bounds.right+1);
  const top = Math.max(0,bounds.top-1), bottom = Math.min(HEIGHT,bounds.bottom+1);
  const header = chart ? unit : 0;
  return {left,right,top,bottom,unit,header,width:Math.ceil((right-left)*unit),height:Math.ceil((bottom-top)*unit+header)};
}
export function captureWritingWorkspace(snapshot, {canvas = document.createElement('canvas'), tens = 'Tens', ones = 'Ones'} = {}) {
  const {rods, chart, showGrid = false} = snapshot;
  const box=writingCaptureBounds(snapshot);
  canvas.width=box.width;canvas.height=box.height;const ctx=canvas.getContext('2d');
  if(!ctx)throw Error('Workspace capture is unavailable in this browser.');
  const {unit,header}=box, x=value=>(value-box.left)*unit, y=value=>(value-box.top)*unit+header;
  ctx.fillStyle='#fff';ctx.fillRect(0,0,canvas.width,canvas.height);
  if(chart){ctx.strokeStyle='#849594';ctx.lineWidth=1;ctx.beginPath();ctx.moveTo(x(WIDTH/2),0);ctx.lineTo(x(WIDTH/2),canvas.height);ctx.stroke();ctx.font=`${Math.min(24,unit*.65)}px Arial`;ctx.textAlign='center';ctx.fillStyle='#203830';ctx.fillText(tens,x(WIDTH/4),unit*.7);ctx.fillText(ones,x(WIDTH*3/4),unit*.7);}
  else if(showGrid){ctx.strokeStyle='#dce6df';ctx.lineWidth=1;ctx.beginPath();for(let column=Math.ceil(box.left);column<=box.right;column++){ctx.moveTo(x(column),0);ctx.lineTo(x(column),canvas.height);}for(let row=Math.ceil(box.top);row<=box.bottom;row++){ctx.moveTo(0,y(row));ctx.lineTo(canvas.width,y(row));}ctx.stroke();}
  for(const rod of rods){const[w,h]=dimensions(rod);ctx.fillStyle=COLORS[rod.length-1];ctx.fillRect(x(rod.x),y(rod.y),w*unit,h*unit);ctx.strokeStyle='#30473a';ctx.lineWidth=1;ctx.strokeRect(x(rod.x)+.5,y(rod.y)+.5,w*unit-1,h*unit-1);}
  return canvas.toDataURL('image/png');
}


// Object provenance belongs to the learner run, never to saved authored scenes.
// Only user changes can introduce these keys; loading setup, demo and answers
// must call retainLearnerObjects (or reset them), never this update function.
export function workspaceObjectKeys(scene) {
  return [
    ...(scene?.items || scene?.rods || []).map(item => `item:${item.id}`),
    ...(scene?.jumps || []).map(jump => `jump:${jump.id}`),
    ...(typeof scene?.marker === 'number' ? ['marker'] : []),
  ];
}
export function retainLearnerObjects(known = [], scene) {
  const present = new Set(workspaceObjectKeys(scene));
  return [...new Set(known)].filter(key => present.has(key));
}
export function updateLearnerObjects(known, previous, next) {
  const prior = new Set(workspaceObjectKeys(previous));
  return retainLearnerObjects([...(known || []), ...workspaceObjectKeys(next).filter(key => !prior.has(key))], next);
}
